/** TODO: determine if this is still in use anywhere. (It may have been added here just for
 * form field wrapper, in which case we don't use this anymore) */

import React, {
  Children,
  useState,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
  ReactElement,
} from 'react'
import { Icon } from 'semantic-ui-react'
import cx from 'classnames'
import cls from './styles.scss'

export const TIME_IT_TAKES_TO_MOVE_MOUSE_FROM_ANCHOR_TO_TOOLTIP = 200 // ms

type TooltipPosition = 'left' | 'right' | 'top' | 'bottom'

interface TooltipProps {
  position: TooltipPosition
  isOpen: boolean
  onClose?: () => void
  children?: ReactNode
  className?: string
  elmId?: string
  error?: boolean
  autoCloseOnMouseLeave?: boolean
  alwaysCloseOnClickOutside?: boolean
}

interface TooltipAnchorProps {
  children: ReactNode
  className?: string
  elmId?: string
}

const Tooltip: React.FC<TooltipProps> = ({
  position,
  isOpen,
  onClose,
  children,
  className,
  elmId,
  error = false,
  autoCloseOnMouseLeave = false,
  alwaysCloseOnClickOutside = false,
}) => {
  const [holdOpen, setHoldOpen] = useState(false)
  const [isMouseOver, setIsMouseOver] = useState(false)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevIsOpenRef = useRef<boolean>(isOpen)

  const supportsTouch = useCallback(() => {
    return (
      typeof document !== 'undefined' &&
      'ontouchstart' in document.documentElement
    )
  }, [])

  const isDescendant = useCallback(
    (parent: Node | null, child: Node): boolean => {
      let node = child.parentNode
      while (node !== null) {
        if (node === parent) {
          return true
        }
        node = node.parentNode
      }
      return false
    },
    []
  )

  const onMouseDown = useCallback(
    (e: MouseEvent | TouchEvent) => {
      // If user clicks outside of tooltip but not on anchor then close when...
      // - "hover" mode is enabled (!!autoCloseOnMouseLeave === true)
      // - OR "click outside to close" mode is enabled (!!alwaysCloseOnClickOutside === true)
      if (
        (autoCloseOnMouseLeave || alwaysCloseOnClickOutside) &&
        tooltipRef.current &&
        !isDescendant(tooltipRef.current.parentNode, e.target as Node) &&
        onClose
      ) {
        onClose()
        setHoldOpen(false)
      }
    },
    [autoCloseOnMouseLeave, alwaysCloseOnClickOutside, onClose, isDescendant]
  )

  // Ref to track isMouseOver for use in timeout callback
  const isMouseOverRef = useRef(isMouseOver)
  isMouseOverRef.current = isMouseOver

  // Handle UNSAFE_componentWillReceiveProps logic
  useEffect(() => {
    /*
      This logic allows for a "hovering tooltip" where the tooltip is opened by hovering
      over the anchor initially - and is kept open by hovering over the tooltip itself.

      Here, if tooltip is closing and autoCloseOnMouseLeave is true, we hold the tooltip
      open for a brief period of time to allow the user to move the mouse from the
      anchor over to the toolip. When the time is up, if the mouse is not over
      the tooltip, we close it (i.e. the user just moused over the anchor and moved
      away without hovering over the tooltip.)

      If the user IS hovering over the tooltip, we hold it open indefinitely, until
      the user mouses away from the tooltip itself (handled in mouseleave event).
     */
    if (autoCloseOnMouseLeave && prevIsOpenRef.current && !isOpen) {
      setHoldOpen(true)
      timeoutIdRef.current = setTimeout(() => {
        if (!isMouseOverRef.current) {
          setHoldOpen(false)
        }
      }, TIME_IT_TAKES_TO_MOVE_MOUSE_FROM_ANCHOR_TO_TOOLTIP)
    }
    prevIsOpenRef.current = isOpen

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current)
      }
    }
  }, [isOpen, autoCloseOnMouseLeave])

  // componentDidMount and componentWillUnmount - add/remove mouse listener
  useEffect(() => {
    const eventType = supportsTouch() ? 'touchstart' : 'mousedown'

    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener(eventType, onMouseDown as EventListener)
    }

    return () => {
      if (typeof document !== 'undefined' && document.removeEventListener) {
        document.removeEventListener(eventType, onMouseDown as EventListener)
      }
    }
  }, [supportsTouch, onMouseDown])

  const handleMouseEnter = useCallback(() => {
    setIsMouseOver(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoldOpen(false)
    setIsMouseOver(false)
  }, [])

  const openCls = isOpen || holdOpen ? cls['is-open'] : null
  const errorCls = error ? cls.error : null

  return (
    <div
      className={cx(cls.tooltip, openCls, cls[position], errorCls, className)}
      data-elm-id={elmId}
      ref={tooltipRef}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      {onClose && !autoCloseOnMouseLeave ? (
        <Icon
          name='close'
          size='small'
          data-elm-id={`${elmId}_close`}
          className={cx(cls['close-icon'])}
          onClick={onClose}
        />
      ) : null}
      <div className={cx(cls.content)} data-elm-id={`${elmId}_content`}>
        {children}
      </div>
      <span className={cx(cls['arrow-border'])} />
      <span className={cx(cls.arrow)} />
    </div>
  )
}

const TooltipAnchor: React.FC<TooltipAnchorProps> = ({
  children,
  className,
  elmId,
}) => {
  const separateTooltipFromChildren = useCallback(() => {
    const childTooltip: ReactElement[] = []
    const otherChildren: ReactNode[] = []

    Children.forEach(children, (child, idx) => {
      if (React.isValidElement(child) && child.type === Tooltip) {
        const clonedChild = React.cloneElement(child as ReactElement, {
          elmId: (child.props as TooltipProps).elmId || elmId,
          key: idx,
        })
        childTooltip.push(clonedChild)
      } else {
        otherChildren.push(child)
      }
    })

    return {
      children: otherChildren,
      tooltip: childTooltip,
    }
  }, [children, elmId])

  const separatedChildren = separateTooltipFromChildren()

  return (
    <div className={cx(cls.root, className)}>
      <div
        className={cx(cls['anchor-content'])}
        data-elm-id={`${elmId}_trigger`}
      >
        {separatedChildren.children}
      </div>
      {separatedChildren.tooltip}
    </div>
  )
}

export default Tooltip
export { TooltipAnchor }
export type { TooltipProps, TooltipAnchorProps }
